import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { geoGraticule10, geoPath, geoProjection, GeoRawProjection } from 'd3-geo';
import { feature } from 'topojson-client';
import type { GeometryCollection } from 'topojson-specification';
import type { Feature, FeatureCollection, Geometry } from 'geojson';
import { CountryRow } from '../../types/census';
import { MetricDef, normalize } from '../../utils/mapMetrics';
import { MAP_RAMPS, rampColor } from '../../utils/color';
import { useTheme } from '../../contexts/ThemeContext';
import { getWorldTopology } from '../../utils/api';
import { indiaClaimFeature } from '../../data/indiaBoundary';
const INDIA_CCN3 = '356';
const W = 1000;
const H = 637;
const COS45 = Math.SQRT1_2;
// Gall–Peters: cylindrical equal-area, standard parallels at ±45°.
const gallPetersRaw: GeoRawProjection = (lambda, phi) => [lambda * COS45, Math.sin(phi) / COS45];
interface Shape {
  key: string;
  ccn3: string | null;
  name: string;
  d: string;
}
interface WorldMapProps {
  rowsByCcn3: Map<string, CountryRow>;
  metric: MetricDef;
  domain: [number, number];
  selected: string | null;
  onSelect: (row: CountryRow) => void;
  onRendered: (matched: number) => void;
}
interface Hover {
  x: number;
  y: number;
  name: string;
  value: string;
}
export function WorldMap({
  rowsByCcn3,
  metric,
  domain,
  selected,
  onSelect,
  onRendered
}: WorldMapProps) {
  const [features, setFeatures] = useState<Feature<Geometry, {
    name?: string;
  }>[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [hover, setHover] = useState<Hover | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const ramp = MAP_RAMPS[useTheme().theme];
  useEffect(() => {
    let alive = true;
    getWorldTopology().then(topo => {
      const fc = feature(topo, topo.objects.countries as GeometryCollection) as unknown as FeatureCollection<Geometry, {
        name?: string;
      }>;
      if (alive) setFeatures(fc.features);
    }).catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, []);
  const path = useMemo(() => geoPath(geoProjection(gallPetersRaw).fitExtent([[6, 6], [W - 6, H - 6]], {
    type: 'Sphere'
  })), []);
  const sphere = useMemo(() => path({
    type: 'Sphere'
  }) ?? '', [path]);
  const graticule = useMemo(() => path(geoGraticule10()) ?? '', [path]);
  const indiaClaim = useMemo(() => path(indiaClaimFeature) ?? '', [path]);
  const shapes: Shape[] = useMemo(() => (features ?? []).map((f, i) => ({
    key: f.id != null ? String(f.id) : `x${i}`,
    ccn3: f.id != null ? String(f.id).padStart(3, '0') : null,
    name: f.properties?.name ?? 'Unknown',
    d: path(f) ?? ''
  })), [features, path]);
  const matched = useMemo(() => shapes.filter(s => s.ccn3 && rowsByCcn3.has(s.ccn3)).length, [shapes, rowsByCcn3]);
  useEffect(() => {
    if (features) onRendered(matched);
  }, [matched, features, onRendered]);
  if (failed) {
    return <div className="flex aspect-[1000/637] w-full flex-col items-center justify-center rounded-lg border border-line bg-surface p-8 text-center" data-id="element-317" data-mp="3eyh12e20zpid">
        <p className="text-ink" data-id="element-318" data-mp="3oxzfyg32wl3k">Map geometry could not be loaded (offline or CDN unavailable).</p>
        <p className="mt-2 max-w-md text-sm text-muted" data-id="element-319" data-mp="3jzegiivni5g6">Every country value is still available in the ranking table, served from the same live or snapshot data.</p>
        <Link to="/countries" className="mt-4 rounded-md border border-line px-3 py-2 text-sm text-ink hover:border-machine" data-id="element-320" data-mp="2w9tz44f5bg90">
          Open the country table →
        </Link>
      </div>;
  }
  if (!features) {
    return <div className="flex aspect-[1000/637] w-full items-center justify-center rounded-lg border border-line bg-surface" role="status" data-id="element-321" data-mp="318x2lkfh9eev">
        <span className="font-mono text-xs text-muted" data-id="element-322" data-mp="2di15c7s95r08">Projecting geometry…</span>
      </div>;
  }
  const valueOf = (s: Shape) => {
    const row = s.ccn3 ? rowsByCcn3.get(s.ccn3) : undefined;
    const v = row ? metric.get(row) : null;
    return {
      row,
      v
    };
  };
  const selectedShape = shapes.find(s => s.ccn3 && rowsByCcn3.get(s.ccn3)?.cca3 === selected);
  return <div ref={wrapRef} className="relative" onMouseLeave={() => setHover(null)} data-id="element-323" data-mp="1kt109eat1u85">
      <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full" role="group" aria-label={`World map, Gall–Peters equal-area projection, colored by ${metric.label}`} data-id="element-324" data-mp="1pjr60sn57z3d">
        <path d={sphere} fill="rgb(var(--c-surface))" stroke="rgb(var(--c-line))" data-id="element-325" data-mp="1ul6qojles3l8" />
        <path d={graticule} fill="none" stroke="rgb(var(--c-line))" strokeWidth={0.5} data-id="element-326" data-mp="1zgm3d87ojtg2" />
        <g data-id="element-327" data-mp="24il5kerookbf">
          {shapes.map(s => {
          const {
            row,
            v
          } = valueOf(s);
          const fill = v != null ? rampColor(normalize(metric, v, domain), ramp) : 'rgb(var(--c-raised))';
          const label = row ? `${row.name}: ${v != null ? metric.format(v) : 'no data'}` : undefined;
          return <path key={s.key} d={s.d} fill={fill} stroke="rgb(var(--c-bg))" strokeWidth={0.5} tabIndex={row ? 0 : -1} role={row ? 'button' : undefined} aria-label={label} className={`outline-none transition-[opacity] duration-150 ${row ? 'cursor-pointer focus-visible:opacity-70' : ''}`} style={{
            opacity: hover && hover.name !== (row?.name ?? s.name) ? 0.88 : 1
          }} onClick={() => row && onSelect(row)} onKeyDown={e => {
            if (row && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              onSelect(row);
            }
          }} onMouseMove={e => {
            const r = wrapRef.current?.getBoundingClientRect();
            if (!r) return;
            setHover({
              x: e.clientX - r.left,
              y: e.clientY - r.top,
              name: row?.name ?? s.name,
              value: v != null ? metric.format(v) : 'No data'
            });
          }} data-id="element-328" data-mp="e6x1xod98ctv" />;
        })}
        </g>
        {selectedShape && <path d={selectedShape.d} fill="none" stroke="rgb(var(--c-ink))" strokeWidth={1.4} pointerEvents="none" data-id="element-329" data-mp="32qdjvoe39w30" />}
        {(() => {
        // India drawn with its official boundary: the claim polygon sits above neighbouring shapes in India's fill.
        const india = rowsByCcn3.get(INDIA_CCN3);
        const v = india ? metric.get(india) : null;
        const fill = v != null ? rampColor(normalize(metric, v, domain), ramp) : 'rgb(var(--c-raised))';
        return <path d={indiaClaim} fill={fill} stroke="none" aria-hidden="true" className={india ? 'cursor-pointer' : ''} style={{
          opacity: hover && hover.name !== (india?.name ?? 'India') ? 0.88 : 1
        }} onClick={() => india && onSelect(india)} onMouseMove={e => {
          const r = wrapRef.current?.getBoundingClientRect();
          if (!r) return;
          setHover({
            x: e.clientX - r.left,
            y: e.clientY - r.top,
            name: india?.name ?? 'India',
            value: v != null ? metric.format(v) : 'No data'
          });
        }} data-id="element-330" data-mp="254g4w0yx48vt" />;
      })()}
      </svg>
      {hover && <div className="pointer-events-none absolute z-10 rounded-md border border-line bg-raised px-2.5 py-1.5 shadow-xl shadow-black/50" style={{
      left: Math.min(hover.x + 14, (wrapRef.current?.clientWidth ?? 600) - 200),
      top: hover.y + 14
    }} data-id="element-331" data-mp="1mdjhg4618js9">
          <p className="whitespace-nowrap text-[12.5px] text-ink" data-id="element-332" data-mp="2a3dtwkmfw6bz">{hover.name}</p>
          <p className="whitespace-nowrap font-mono text-[11.5px] text-machine" data-id="element-333" data-mp="254sv8pmu5upn">{hover.value}</p>
        </div>}
    </div>;
}